import type { CSSProperties, FC, ReactNode, SVGProps } from 'react'
import type { Sprite } from '@/components/pixel/sprites'

import { useMemo } from 'react'
import { cn } from '@/lib/utils'
import { spritePaths } from '@/components/pixel/sprite-paths'

interface PixelSpriteProps extends Omit<SVGProps<SVGSVGElement>, 'children'> {
    sprite: Sprite
    label?: string
}

interface AnimatedSpriteProps {
    frames: [Sprite, Sprite]
    label?: string
    className?: string
    style?: CSSProperties
}

export const PixelSprite: FC<PixelSpriteProps> = ({ sprite, label, ...rest }): ReactNode => {
    const paths = useMemo(() => spritePaths(sprite), [sprite])

    return (
        <svg
            viewBox={`0 0 ${sprite.width} ${sprite.height}`}
            shapeRendering='crispEdges'
            role={label ? 'img' : undefined}
            aria-label={label}
            aria-hidden={label ? undefined : true}
            focusable='false'
            {...rest}
        >
            {paths.map((path) => (
                <path key={path.fill} fill={path.fill} d={path.d} />
            ))}
        </svg>
    )
}

// Two frames swapped with CSS, used for the flickering fires.
export const AnimatedSprite: FC<AnimatedSpriteProps> = ({ frames, label, className, style }): ReactNode => {
    const [first, second] = frames

    return (
        <span
            className={cn('relative inline-block', className)}
            style={{ aspectRatio: `${first.width} / ${first.height}`, ...style }}
            role={label ? 'img' : undefined}
            aria-label={label}
            aria-hidden={label ? undefined : true}
        >
            <PixelSprite sprite={first} className='df-frame-a absolute inset-0 size-full' />
            <PixelSprite sprite={second} className='df-frame-b absolute inset-0 size-full' />
        </span>
    )
}
